import { useMemo, useSyncExternalStore } from 'react';
import type { SignalReadState } from './signals.js';

// Read, dismissed and pinned live in this browser, one entry per signed-in user, until the
// server keeps them (see .claude/patterns/signals-read-state.md). Not shared across devices.
export const SIGNAL_STORAGE_PREFIX = 'alison.signals.v2:';

// A mark is kept this long after it was first made; must stay ≥ the feed window (FEED_WINDOW_DAYS).
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const EMPTY: StoredState = { read: [], dismissed: [], markedAt: {}, pinned: false };
const NO_USER_STATE: SignalState = { read: new Set(), dismissed: new Set(), pinned: false };
// No user yet (session loading): nothing to show and nowhere to write.
const NO_USER: Store = {
  getSnapshot: () => NO_USER_STATE,
  subscribe: () => () => {},
  update: () => {},
  reload: () => {},
};
const stores = new Map<string, Store>();
let listeningToOtherTabs = false;

interface StoredState {
  read: string[];
  dismissed: string[];
  // Run id → when it was first read or dismissed (epoch ms).
  markedAt: Record<string, number>;
  pinned: boolean;
}

interface Store {
  getSnapshot: () => SignalState;
  subscribe: (listener: () => void) => () => void;
  update: (change: (stored: StoredState) => StoredState) => void;
  reload: () => void;
}

export interface SignalState extends SignalReadState {
  pinned: boolean;
}

export interface SignalStateControls {
  state: SignalState;
  markRead: (runId: string) => void;
  dismiss: (runId: string) => void;
  restore: (runId: string) => void;
  setPinned: (pinned: boolean) => void;
}

function withId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids : [...ids, id];
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function timestamps(value: unknown): Record<string, number> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, number] => Number.isFinite(entry[1])),
  );
}

export function parseStored(raw: string | null): StoredState {
  if (raw === null) return EMPTY;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof StoredState, unknown>> | null;
    return {
      read: strings(parsed?.read),
      dismissed: strings(parsed?.dismissed),
      markedAt: timestamps(parsed?.markedAt),
      pinned: parsed?.pinned === true,
    };
  } catch {
    return EMPTY;
  }
}

// Drops marks older than the retention window. An id with no timestamp (new, or stored before
// timestamps existed) is stamped now, so it gets a full window.
function pruned(stored: StoredState, now: number): StoredState {
  const markedAt: Record<string, number> = {};
  for (const id of new Set([...stored.read, ...stored.dismissed])) {
    const at = Object.hasOwn(stored.markedAt, id) ? (stored.markedAt[id] as number) : now;
    if (now - at < RETENTION_MS) markedAt[id] = at;
  }
  const kept = (id: string): boolean => Object.hasOwn(markedAt, id);
  return {
    ...stored,
    read: stored.read.filter(kept),
    dismissed: stored.dismissed.filter(kept),
    markedAt,
  };
}

function readStorage(key: string): StoredState {
  try {
    return parseStored(window.localStorage.getItem(key));
  } catch {
    return EMPTY;
  }
}

function toState(stored: StoredState): SignalState {
  return {
    read: new Set(stored.read),
    dismissed: new Set(stored.dismissed),
    pinned: stored.pinned,
  };
}

function createStore(key: string): Store {
  const listeners = new Set<() => void>();
  // This page's copy is the source of truth; storage persists it and carries it to other tabs.
  let stored: StoredState | null = null;
  let snapshot: SignalState | null = null;
  const notify = (): void => {
    for (const listener of listeners) listener();
  };
  return {
    getSnapshot: () => {
      stored ??= readStorage(key);
      snapshot ??= toState(stored);
      return snapshot;
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    update: (change) => {
      const current = stored ?? readStorage(key);
      const next = pruned(change(current), Date.now());
      // An update that changes nothing must not notify, or an effect that marks a run read loops.
      if (JSON.stringify(next) === JSON.stringify(current) && snapshot !== null) return;
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage full or blocked: the change still applies to this page.
      }
      stored = next;
      snapshot = toState(next);
      notify();
    },
    reload: () => {
      stored = null;
      snapshot = null;
      notify();
    },
  };
}

// Another tab wrote this user's state: drop the cached snapshot so this tab rereads it.
function listenToOtherTabs(): void {
  if (listeningToOtherTabs || typeof window === 'undefined') return;
  listeningToOtherTabs = true;
  window.addEventListener('storage', (event) => {
    if (event.key === null) {
      for (const store of stores.values()) store.reload();
      return;
    }
    stores.get(event.key)?.reload();
  });
}

function storeFor(userKey: string): Store {
  const key = `${SIGNAL_STORAGE_PREFIX}${userKey}`;
  let store = stores.get(key);
  if (store === undefined) {
    store = createStore(key);
    stores.set(key, store);
  }
  listenToOtherTabs();
  return store;
}

export function useSignalState(userKey: string | null): SignalStateControls {
  const store = userKey === null ? NO_USER : storeFor(userKey);
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  // Stable per user, not per state, so effects may depend on them.
  const actions = useMemo(
    () => ({
      markRead: (runId: string) =>
        store.update((stored) => ({ ...stored, read: withId(stored.read, runId) })),
      dismiss: (runId: string) =>
        store.update((stored) => ({
          ...stored,
          read: withId(stored.read, runId),
          dismissed: withId(stored.dismissed, runId),
        })),
      restore: (runId: string) =>
        store.update((stored) => ({
          ...stored,
          dismissed: stored.dismissed.filter((id) => id !== runId),
        })),
      setPinned: (pinned: boolean) => store.update((stored) => ({ ...stored, pinned })),
    }),
    [store],
  );
  return useMemo(() => ({ state, ...actions }), [state, actions]);
}

export function resetSignalStoresForTests(): void {
  stores.clear();
}
